import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { closedDaysLabel, hoursLabel } from '../../data/content';
import { useSite } from '../../contexts/SiteContext';
import { mainNav } from '../../data/navigation';
import { legalDocs } from '../../data/legal';
import { policyCopy } from '../../data/policies';
import { shopLabels } from '../../data/shopLabels';
import { isOpen } from '../../core/rules/schedule';
import { ease } from '../../core/format/motion';
import { instagramHandle, lowerFirst } from '../../core/format/text';
import { NewsletterSignup } from './NewsletterSignup';
const colHeading = 'text-[13px] text-shell';
const link = 'underline-offset-4 transition-[text-decoration-color] duration-150 ease-soft hover:underline';

/**
 * De footer, op klei: het donkere slot van elke pagina. Nieuwsbrief groot bovenaan, daaronder
 * vier rustige kolommen (wie, bezoek, tijden, ontdek) en als afsluiting de naam van de salon
 * over de volle breedte, toonsgewijs in het klei: een signatuur, geen extra informatie.
 *
 * Contrast: alle tekst shell op klei (5,6:1). De focusring is hier shell in plaats van klei.
 */
export function Footer() {
  const reduce = useReducedMotion();
  // Gegevens en tijden uit de actuele instellingen (Instellingen › Salon & contact en › Salondagen).
  const {
    salon,
    openingHours
  } = useSite();
  return <footer className="overflow-hidden bg-clay text-shell [&_*:focus-visible]:outline-shell" data-id="element-7751" data-mp="1w90uo3y4up7i">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7752" data-mp="209ujczjeg81g">
        <NewsletterSignup data-id="element-7753" data-mp="20788or17pcf0" />

        <div className="grid grid-cols-2 gap-x-6 gap-y-12 border-t border-shell/25 py-14 lg:grid-cols-12 lg:gap-x-10 lg:py-16" data-id="element-7754" data-mp="1mabdyehz3asu">
          <div className="col-span-2 lg:col-span-4" data-id="element-7755" data-mp="1r7lbyijfgusa">
            <p className="font-display text-[1.6rem] font-light leading-tight" data-id="element-7756" data-mp="auwff6a48avm">{salon.name}</p>
            <p className="mt-3 max-w-sm text-[15px] leading-relaxed" data-id="element-7757" data-mp="ftpqks1xjpsm">
              Huidverbetering en 100% vegan skincare in {salon.city}. In de salon behandeld, thuis met de producten die ik zelf gebruik.
            </p>
            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[15px]" data-id="element-7758" data-mp="3nv5leh1n345s">
              {salon.instagram && <li data-id="element-7759" data-mp="3j3e652nxokuy">
                  <a href={salon.instagram} target="_blank" rel="noreferrer" className={`inline-flex min-h-[44px] items-center gap-1.5 ${link}`} data-id="element-7760" data-mp="3j2uo7lya9wgx">
                    Instagram <span className="text-shell" data-id="element-7761" data-mp="396rmuib9a1v1">@{instagramHandle(salon.instagram, salon.name)}</span>
                    <ArrowUpRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7762" data-mp="34757a27yg9j2" />
                    <span className="sr-only" data-id="element-7763" data-mp="3051m2lvhhqkl"> {shopLabels.newTab}</span>
                  </a>
                </li>}
              <li data-id="element-7764" data-mp="2v8b5lxfrosyn">
                <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`inline-flex min-h-[44px] items-center gap-1.5 ${link}`} data-id="element-7765" data-mp="2v8t6j7ddjvcw">
                  WhatsApp
                  <ArrowUpRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7766" data-mp="tfwp92svfwkc" />
                  <span className="sr-only" data-id="element-7767" data-mp="10yd0ukkecg3e"> {shopLabels.newTab}</span>
                </a>
              </li>
            </ul>
          </div>

          <div className="col-span-2 sm:col-span-1 lg:col-span-3" data-id="element-7768" data-mp="1aopj264qnx6d">
            <h2 className={colHeading} data-id="element-7769" data-mp="1aphffj65tyxp">
              <Link to="/contact" className={link} data-id="element-7770" data-mp="1fniyi43l5y96">
                Bezoek & contact
              </Link>
            </h2>
            <address className="mt-4 space-y-3 text-[15px] not-italic leading-relaxed" data-id="element-7771" data-mp="i0tpvbedonec">
              <p data-id="element-7772" data-mp="mylgm23ae3gy">
                {salon.street}
                <br data-id="element-7773" data-mp="mzdbfap27x66" />
                {salon.postalCode} {salon.locality}
                <br data-id="element-7774" data-mp="rxeu3uikpuh4" />
                <a href={salon.routeUrl} target="_blank" rel="noreferrer" className={`inline-flex min-h-[44px] items-center underline ${link}`} data-id="element-7775" data-mp="rwwqudxhufzp">
                  Plan je route<span className="sr-only" data-id="element-7776" data-mp="23e4s4qcuffzn"> {shopLabels.newTab}</span>
                </a>
              </p>
              <p data-id="element-7777" data-mp="27dx6jjox4gcx">
                <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={link} data-id="element-7778" data-mp="27ddlvv7vvdv7">
                  <span className="sr-only" data-id="element-7779" data-mp="25ykp6uhsttq4">WhatsApp: </span>
                  {salon.phone}
                  <span className="sr-only" data-id="element-7780" data-mp="20zzzhp06asfx"> {shopLabels.newTab}</span>
                </a>
                <br data-id="element-7781" data-mp="2fx0gaxgtt2zm" />
                <a href={`mailto:${salon.email}`} className={`break-all ${link}`} data-id="element-7782" data-mp="2fxjy8e6h7rdn">
                  <span className="sr-only" data-id="element-7783" data-mp="2avupx6an1rt9">E-mail: </span>
                  {salon.email}
                </a>
              </p>
            </address>
            <p className="mt-4 text-[13px] leading-relaxed" data-id="element-7784" data-mp="2ouav2ssura9a">{policyCopy.response}</p>
            {salon.englishNote && <p className="mt-1 text-[13px] leading-relaxed" data-id="element-7785" data-mp="2ymgsii1vijvv">{salon.englishNote}</p>}
          </div>

          <div className="col-span-2 sm:col-span-1 lg:col-span-3" data-id="element-7786" data-mp="2tndmaua5q7mb">
            <h2 className={colHeading} data-id="element-7787" data-mp="2to5io7bkw9dn">Salon, op afspraak</h2>
            <dl className="mt-4 text-[15px]" data-id="element-7788" data-mp="37mda5zpuiihf">
              {openingHours.filter(isOpen).map(h => <div key={h.day} className="flex justify-between gap-4 border-b border-shell/20 py-2" data-id="element-7789" data-mp="33mkznhgw629f">
                  <dt data-id="element-7790" data-mp="3mb5algmsg97b">{h.day}</dt>
                  <dd className="tabular-nums" data-id="element-7791" data-mp="3m7r5w1qnjnwp">{hoursLabel(h)}</dd>
                </div>)}
            </dl>
            <p className="mt-3 text-[13px]" data-id="element-7792" data-mp="3d95rmis7qv1q">{closedDaysLabel(openingHours)}</p>
            <p className="mt-1 text-[13px]" data-id="element-7793" data-mp="3db9t6u2n3hyu">De webshop is altijd open: {lowerFirst(policyCopy.delivery)}.</p>
          </div>

          <nav aria-label="Footernavigatie" className="col-span-2 lg:col-span-2" data-id="element-7794" data-mp="33jm9jfcbr57x">
            <h2 className={colHeading} data-id="element-7795" data-mp="33hj7ty1t7nnd">Ontdek</h2>
            <ul className="mt-2 grid grid-cols-2 text-[15px] lg:mt-3 lg:grid-cols-1" data-id="element-7796" data-mp="38b47duj1wpoo">
              {mainNav.map(l => <li key={l.to} data-id="element-7797" data-mp="2ukipa81f8jru">
                  <Link to={l.to} className={`inline-flex min-h-[44px] items-center lg:min-h-[36px] ${link}`} data-id="element-7798" data-mp="2yk9ck1rqohrm">
                    {l.label}
                  </Link>
                </li>)}
            </ul>
          </nav>
        </div>
      </div>

      {/* De signatuur: de naam over de volle breedte, toonsgewijs. Decoratie; de naam staat hierboven al als tekst. */}
      <div aria-hidden="true" className="select-none overflow-hidden" data-id="element-7799" data-mp="v5h4s0moz7xw">
        <motion.p initial={reduce ? false : {
        y: '35%',
        opacity: 0
      }} whileInView={{
        y: '0%',
        opacity: 1
      }} viewport={{
        once: true,
        margin: '0px 0px -10% 0px'
      }} transition={{
        duration: 0.3,
        ease
      }} className="whitespace-nowrap text-center font-display text-[17vw] font-light leading-[0.78] tracking-[-0.045em] text-shell/[0.14] lg:text-[16.5vw] 2xl:text-[15rem]" data-id="element-7800" data-mp="qb34pkx34cmw">
          {salon.name}
        </motion.p>
      </div>

      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7801" data-mp="150r42glph17z">
        <div className="flex flex-col gap-3 border-t border-shell/25 py-6 text-[13px] sm:flex-row sm:items-center sm:justify-between" data-id="element-7802" data-mp="102fxceqpj6tz">
          <p data-id="element-7803" data-mp="3ps1cafwzlzwy">
            © {new Date().getFullYear()} {salon.name} · KvK {salon.kvk}
            {salon.vatId && <> · Btw-id {salon.vatId}</>}
          </p>
          {legalDocs.length > 0 && <nav aria-label="Juridisch" data-id="element-7804" data-mp="3apj8an785tat">
              <ul className="flex flex-wrap gap-x-5 sm:gap-y-2" data-id="element-7805" data-mp="3aqkqrcqkli2k">
                {legalDocs.map(d => <li key={d.path} data-id="element-7806" data-mp="3flqb474bqooj">
                    <Link to={d.path} className={`inline-flex min-h-[44px] items-center sm:min-h-0 ${link}`} data-id="element-7807" data-mp="3kme2itwgt7ja">
                      {d.title}
                    </Link>
                  </li>)}
              </ul>
            </nav>}
        </div>
      </div>
    </footer>;
}